import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Check,
  Clock,
  MessageCircle,
  Stethoscope,
  Zap,
} from 'lucide-react';
import { Alert, Loading } from 'react-daisyui';
import { getConfirmationState } from 'components/Appointment/helper';
import { ActivityTimeline } from 'components/Appointment/ActivityTimeline';
import { AddToCalendar } from 'components/Appointment/AddToCalendar';
import { StatusBadge } from 'components/Appointment/StatusBadge';
import { ConfirmationBadge } from 'components/Appointment/ConfirmationBadge';
import ConversationPopup from 'components/Appointment/ConversationPopup';
import ConfirmAppointmentModal from 'components/Appointment/ConfirmAppointmentModal';
import AppointmentModal from 'components/Appointment/AppointmentModal';
import PostOpInstructions from 'components/Appointment/PostOpInstructions';
import { PortalSpinner } from 'components/PortalSpinner';
import { formatDate, timeAgo } from 'utils/helpers';
import { APPOINTMENT_STATUS, PORTAL_ROUTES } from 'utils/constants';
import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AppointmentActions } from 'store/actions';
import toast from 'react-hot-toast';
import { requestMapper } from 'store/Appointments/helper';
import { getPractice } from 'library/auth';
const enhancer = connect(null, { ...AppointmentActions })

type StatusKind =
  | 'scheduled-pending'
  | 'scheduled-confirmed'
  | 'completed'
  | 'cancelled'
  | 'unscheduled'
  | 'other';

const resolveStatus = (appt): StatusKind => {
  if (appt.status === APPOINTMENT_STATUS.CANCELLED) return 'cancelled';
  if (appt.status === APPOINTMENT_STATUS.COMPLETED) return 'completed';
  if (appt.status === APPOINTMENT_STATUS.UNSCHEDULED) return 'unscheduled';
  if (appt.status === APPOINTMENT_STATUS.SCHEDULED)
    return appt.confirmed ? 'scheduled-confirmed' : 'scheduled-pending';
  return 'other';
};

// Confirmation accent — a single colored stripe at the top of the visit card.
// Keeps content/icons neutral; the stripe is the only chromatic cue for confirmation state.
const CONFIRMATION_STRIPE: Record<StatusKind, string> = {
  'scheduled-pending': 'bg-amber-400',
  'scheduled-confirmed': 'bg-green-500',
  completed: 'bg-blue-500',
  cancelled: 'bg-red-500',
  unscheduled: 'bg-gray-300',
  other: 'bg-gray-300',
};

const ERROR_MESSAGE = "Couldn't load this appointment. Please try again"

const AppointmentShow = ({ getAppointment, updateAppointment }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { encodedResourceId } = useParams();
  const [confirming, setConfirming] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [appointment, setAppointment] = useState(null)
  const [conversationOpen, setConversationOpen] = useState(false)

  const { content, time, fromPatient } = appointment?.latestConversation?.lastMessage || {}

  useEffect(() => {
    if (encodedResourceId)
      fetchAppointment(encodedResourceId)
  }, [encodedResourceId])

  const fetchAppointment = useCallback(async (id) => {
    setLoading(true)
    try {
      const { data } = await getAppointment(id);
      setAppointment(data);

    } catch (error) {
      toast.error(error.errors || ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [])

  const openConfirmAppointmentModal = useCallback(() => {
    setShowConfirmModal(true)
  }, [])

  const closeConfirmAppointmentModal = useCallback(() => {
    if (confirming) return;
    setShowConfirmModal(false)
  }, [confirming])

  const confirmAppointment = useCallback(async () => {
    setConfirming(true)

    try {
      let { data } = await updateAppointment(encodedResourceId, 'confirm')

      const request = requestMapper(data);
      setAppointment(appointment => ({ ...appointment, requests: [request, ...(appointment.requests || [])] }))

    } catch (error) {
      toast.error(error.errors || 'Failed to confirm appointment. Please try again later.');
      throw error;
    } finally {
      setConfirming(false)
    }
  }, [encodedResourceId, updateAppointment])

  if (loading) {
    return <PortalSpinner padding="py-20" />;
  }

  function goBack() {
    const from = (location.state as { from?: string } | null)?.from;

    if (from?.startsWith(PORTAL_ROUTES.APPOINTMENTS)) {
      navigate(-1);
      return;
    }

    navigate(PORTAL_ROUTES.APPOINTMENTS);
  }

  if (!appointment) {
    return (
      <div className="space-y-4">
        <button
          onClick={goBack}
          className="flex items-center gap-1.5 text-sm text-secondary-500 hover:text-secondary-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Appointments
        </button>
        <div className="py-12 text-center text-sm text-secondary-400">
          {ERROR_MESSAGE}
        </div>
      </div>
    );
  }

  const kind = resolveStatus(appointment);

  const isUpcoming = kind === 'scheduled-pending' || kind === 'scheduled-confirmed';

  // Confirm button visibility is centralized in canRequestConfirm — same
  // rule used by the appointments table and the next-appointment card.
  const confirmationState = getConfirmationState(appointment);

  const formsAccess = Boolean(getPractice()?.patient_forms_access);

  return (
    <div className="space-y-4">
      <button
        onClick={goBack}
        className="flex items-center gap-1.5 text-sm text-secondary-500 hover:text-secondary-800 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Appointments
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ============================ LEFT (main) ============================ */}
        <div className="lg:col-span-2 space-y-4">
          {/* Visit details */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Confirmation accent stripe */}
            <div className={`h-1 ${CONFIRMATION_STRIPE[kind]}`} aria-hidden="true" />

            <div className="p-5">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <Calendar size={20} className="text-secondary-600" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900">
                  {formatDate(appointment.appointmentAt, 'long')}
                </h2>
                {kind === 'scheduled-confirmed' ? (
                  <ConfirmationBadge appt={appointment} variant="pill" />
                ) : (
                  <StatusBadge status={appointment.status} variant="pill" />
                )}
              </div>

              <div className="border-t border-gray-100 my-4" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-400">
                    <Stethoscope size={12} />
                    {appointment.provider.role || 'Provider'}
                  </div>
                  <p className="text-sm font-semibold text-secondary-900 mt-1 truncate">
                    {appointment.provider.name || 'Not assigned'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-400">
                    <Clock size={12} />
                    Time
                  </div>
                  <p className="text-sm font-semibold text-secondary-900 mt-1">
                    {appointment.startTime}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <PostOpInstructions postOpInstruction={appointment.postOpInstruction} />
        </div>

        {/* ============================ RIGHT (rail) ============================ */}
        <div className="space-y-4">
          {/* Quick actions */}
          {isUpcoming && (
            <div className="relative overflow-visible bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-teamcare-blue/10 blur-3xl" />
              </div>
              <div className="relative">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teamcare-blue to-teamcare-blue-dark flex items-center justify-center shadow-sm shadow-blue-200/60">
                    <Zap size={18} className="text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-base font-bold text-secondary-900">Quick Actions</h3>
                </div>
                <p className="text-xs text-secondary-500 mt-3 leading-relaxed">
                  Available actions for this appointment.
                </p>

                <div className="mt-4 space-y-2">
                  {confirmationState === 'pending' ? (
                    <button
                      onClick={openConfirmAppointmentModal}
                      disabled={confirming}
                      className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-br from-green-500 to-green-600 rounded-lg px-3 py-2.5 hover:from-green-600 hover:to-green-700 shadow-sm shadow-green-200/60 transition-all disabled:opacity-60 disabled:cursor-wait ${confirming ? '' : 'animate-ping-soft motion-reduce:animate-none'
                        }`}
                    >
                      <Check size={15} strokeWidth={2.5} />
                      Confirm Appointment
                    </button>
                  ) : confirmationState === 'pending-request' ? (
                    <Alert
                      role="status"
                      icon={<Loading variant="spinner" size="xs" className="shrink-0" />}
                      className="flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-700"
                    >
                      Office to confirm
                    </Alert>
                  ) : confirmationState === 'confirmed' ? (
                    <Alert
                      role="status"
                      icon={<Check size={15} strokeWidth={2.5} />}
                      className="flex items-center justify-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm font-semibold text-green-700"
                    >
                      Appointment confirmed
                    </Alert>
                  ) : null}

                  <AddToCalendar
                    appointment={appointment}
                    wrapperClassName="w-full"
                    triggerClassName="w-full py-2.5 rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          <ActivityTimeline appointment={appointment} />

          {/* Messages */}
          {appointment.latestConversation && (
            <button
              onClick={() => setConversationOpen(true)}
              className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <MessageCircle size={18} className="text-teamcare-blue" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-secondary-900">Messages</span>
                    <span className="text-xs text-secondary-400 shrink-0">
                      {timeAgo(time)}
                    </span>
                  </div>
                  <p className="text-xs text-secondary-500 truncate mt-0.5">
                    <span className="font-medium text-secondary-900">{fromPatient === true ? 'You: ' : ''}</span>
                    {content}
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      {conversationOpen && 
        <ConversationPopup
          conversation={appointment.latestConversation}
          onClose={() => setConversationOpen(false)}
        />
      }
      
      {formsAccess ? (
        <ConfirmAppointmentModal
          open={showConfirmModal}
          appointment={appointment}
          loading={confirming}
          onClose={closeConfirmAppointmentModal}
          onConfirm={confirmAppointment}
        />
      ) : (
        <AppointmentModal
          open={showConfirmModal}
          loading={confirming}
          onClose={closeConfirmAppointmentModal}
          onConfirm={confirmAppointment}
        />
      )}
    </div>
  );
};

export default enhancer(AppointmentShow)
